import { useState } from "react";
import { Card } from "@/components/ui/card";
import Pagination from "@/components/Pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowRight, ClipboardList, CheckCircle2, PauseCircle, MessageSquareText } from "lucide-react";
import { MoneyCell } from "./money";
import { LogCallButton } from "../calls/CallHistory";

const STATUS = {
  initiated: { label: "RFQ Initiated", cls: "bg-indigo-100 text-indigo-800" },
  yet_to_start: { label: "Yet to Start", cls: "bg-red-100 text-red-800" },
  in_progress: { label: "In Progress", cls: "bg-yellow-100 text-yellow-800" },
  completed: { label: "Completed", cls: "bg-blue-100 text-blue-800" },
  reviewed: { label: "Reviewed", cls: "bg-emerald-100 text-emerald-800" },
  hold: { label: "Hold", cls: "bg-amber-200 text-amber-900" },
};

const PAGE_SIZE = 5;

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const RefCell = ({ item }) => (
  <div className="flex items-center gap-2 whitespace-nowrap">
    {item.kind === "rfq" ? (
      <>
        <span className="text-indigo-700 font-medium">{item.rfq_number}</span>
        <Badge className="bg-indigo-600 text-white text-[10px] px-1.5 py-0.5">RFQ</Badge>
      </>
    ) : (
      <div>
        <div className="font-medium">{item.estimation_number}</div>
        <div className="text-xs text-gray-500">v{item.version}</div>
      </div>
    )}
    {item.high_priority && <Badge className="bg-red-600 text-white text-[10px] px-1.5 py-0.5">HIGH</Badge>}
  </div>
);

const HoldReason = ({ item }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-amber-800" disabled={!item.status_reason} data-testid={`followup-hold-reason-btn-${item.estimation_number}`}>
        <MessageSquareText className="h-3.5 w-3.5 mr-1" /> {item.status_reason ? "View" : "None"}
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-72 bg-white text-sm whitespace-pre-wrap" data-testid={`followup-hold-reason-${item.estimation_number}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-amber-800 mb-1">Hold Reason</div>
      {item.status_reason}
    </PopoverContent>
  </Popover>
);

const ActionCell = ({ item, canConvertRfq, onConvert }) => (
  <TableCell className="whitespace-nowrap">
    {item.client_id && <LogCallButton entityType="client" entityId={item.client_id} name={item.client_name} />}
    {item.kind === "rfq" && canConvertRfq ? (
      <Button size="sm" variant="outline" className="rounded-md3-full text-xs h-7" data-testid={`sales-convert-rfq-${item.rfq_id}`} onClick={() => onConvert(item.rfq_id)}>
        Convert <ArrowRight className="h-3 w-3 ml-1" />
      </Button>
    ) : null}
  </TableCell>
);

const FollowUpRow = ({ item, canConvertRfq, onConvert, onHold }) => {
  const isRfq = item.kind === "rfq";
  const overdue = item.due_date && item.due_date < today();
  const st = STATUS[item.submission_status] || { label: item.submission_status, cls: "bg-gray-100 text-gray-800" };
  return (
    <TableRow
      className={item.high_priority ? "bg-red-50 border-l-4 border-l-red-500" : "bg-white"}
      data-testid={isRfq ? `sales-followup-rfq-${item.rfq_id}` : `sales-followup-est-${item.estimation_number}`}
    >
      <TableCell className={`whitespace-nowrap font-semibold ${!item.due_date ? "text-gray-400 font-normal" : overdue ? "text-red-700" : "text-orange-700"}`} data-testid="followup-due-date">
        {item.due_date || "-"}
        {overdue && <div className="text-[10px] font-medium uppercase tracking-wide text-red-600">Overdue</div>}
      </TableCell>
      <TableCell className="whitespace-nowrap" data-testid="followup-received-date">{item.estimation_received_date || "-"}</TableCell>
      <TableCell><RefCell item={item} /></TableCell>
      <TableCell>{item.client_name}</TableCell>
      <TableCell>{item.project_name}</TableCell>
      <TableCell>{isRfq ? "-" : <MoneyCell usd={item.estimated_total_cost} native={item.native_total_cost} currency={item.currency} />}</TableCell>
      <TableCell className="whitespace-nowrap">{item.estimated_total_schedule}</TableCell>
      <TableCell><span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${st.cls}`}>{st.label}</span></TableCell>
      {onHold ? (
        <>
          <TableCell className="whitespace-nowrap" data-testid={`followup-hold-date-${item.estimation_number}`}>{item.status_date || "-"}</TableCell>
          <TableCell className="whitespace-nowrap"><HoldReason item={item} />{item.client_id && <LogCallButton entityType="client" entityId={item.client_id} name={item.client_name} />}</TableCell>
        </>
      ) : <ActionCell item={item} canConvertRfq={canConvertRfq} onConvert={onConvert} />}
    </TableRow>
  );
};

const ACTIVE_SUMMARY = "not yet submitted — open RFQs and estimations that are Yet to Start, In Progress, Completed or Reviewed, earliest due date first";

export const FollowUpCard = ({ items: allItems, canConvertRfq, onConvert }) => {
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState("active");
  const holdItems = allItems.filter((i) => i.submission_status === "hold");
  const activeItems = allItems.filter((i) => i.submission_status !== "hold");
  const onHold = tab === "hold";
  const items = onHold ? holdItems : activeItems;
  const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pageCount);
  const paged = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const plural = items.length !== 1 ? "s" : "";
  return (
  <Card className="p-6 mb-6 bg-amber-50 border-0 shadow-md3-2 rounded-md3-lg" data-testid="sales-followup-card">
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <h2 className="text-md3-title-large text-orange-900 flex items-center gap-2" data-testid="sales-followup-title">
        <ClipboardList className="h-6 w-6" /> Follow-up Required
      </h2>
      <Tabs value={tab} onValueChange={(v) => { setTab(v); setPage(1); }}>
        <TabsList className="bg-orange-100">
          <TabsTrigger value="active" data-testid="sales-followup-tab-active">Active ({activeItems.length})</TabsTrigger>
          <TabsTrigger value="hold" data-testid="sales-followup-tab-hold"><PauseCircle className="h-3.5 w-3.5 mr-1" /> On Hold ({holdItems.length})</TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
    {items.length === 0 ? (
      <div className="text-center py-6 text-md3-on-surface-variant bg-md3-surface-container rounded-md3-md" data-testid="sales-followup-empty">
        <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-green-600" />
        <span className="text-md3-body-medium">{onHold ? "No estimations are on hold." : "Nothing pending — every RFQ and estimation has been submitted."}</span>
      </div>
    ) : (
      <>
        <p className="text-md3-body-medium text-orange-800 mb-4 bg-orange-100 rounded-md3-sm px-4 py-2 border-l-4 border-orange-500" data-testid="sales-followup-summary">
          {onHold ? `${items.length} estimation${plural} on hold, earliest due date first` : `${items.length} item${plural} ${ACTIVE_SUMMARY}`}
        </p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Due Date</TableHead>
              <TableHead>Received</TableHead>
              <TableHead>Estimation / RFQ #</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Project Name</TableHead>
              <TableHead className="whitespace-nowrap">Total Cost</TableHead>
              <TableHead>Schedule</TableHead>
              <TableHead>Status</TableHead>
              {onHold ? (<><TableHead className="whitespace-nowrap">Hold Date</TableHead><TableHead>Reason</TableHead></>) : <TableHead>Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.map((item) => (
              <FollowUpRow key={item.rfq_id || item.id || item.estimation_number} item={item} canConvertRfq={canConvertRfq} onConvert={onConvert} onHold={onHold} />
            ))}
          </TableBody>
        </Table>
        {items.length > PAGE_SIZE && (
          <Pagination
            currentPage={current}
            totalItems={items.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            showPageSizeSelector={false}
            testIdPrefix="sales-followup-pagination"
          />
        )}
      </>
    )}
  </Card>
  );
};
